import React, { useState, useCallback, useEffect } from 'react';
import {
    Card,
    Layout,
    Text,
    Badge,
    Icon,
    InlineStack,
} from '@shopify/polaris';
import {
    ContentMinor,
    PlayMinor,
    GaugeMinor,
    ImageMajor
} from '@shopify/polaris-icons';
import '@shopify/polaris/build/esm/styles.css';
import '../modal-overrides.css';
import '../dashboard.css';

// Import services
import { authService } from '../services/authService';
import { getServerURL } from '../services/config';

const SERVER_URL = getServerURL();


// 2. Add a custom animated LiveIcon SVG for GPU status
const LiveIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ verticalAlign: 'middle', marginLeft: 5, marginRight: 5 }}>
        <circle cx="10" cy="10" r="7" stroke="#dc2626" strokeWidth="2" fill="none" opacity="0.5">
            <animate attributeName="r" values="7;9;7" dur="1s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.5;1;0.5" dur="1s" repeatCount="indefinite" />
        </circle>
        <circle cx="10" cy="10" r="4" fill="#dc2626">
            <animate attributeName="opacity" values="1;0.7;1" dur="1s" repeatCount="indefinite" />
        </circle>
    </svg>
);

const DatabaseIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="10" cy="5" rx="7" ry="3" stroke="currentColor" strokeWidth="1.5" fill="none" />
        <path d="M3 5v5c0 1.657 3.134 3 7 3s7-1.343 7-3V5" stroke="currentColor" strokeWidth="1.5" fill="none" />
        <path d="M3 10v5c0 1.657 3.134 3 7 3s7-1.343 7-3v-5" stroke="currentColor" strokeWidth="1.5" fill="none" />
    </svg>
);
// Replace renderDashboardContent with a modern, visually appealing dashboard
const DashboardPage = () => {

    const [systemStatus, setSystemStatus] = useState(null);

    const fetchSystemStatus = useCallback(async () => {
        try {
            const response = await fetch(`${SERVER_URL}/system-status`, {
                headers: authService.getAuthHeaders(),
            });
            const result = await response.json();
            setSystemStatus(result);
        } catch (error) {
            setSystemStatus(null);
        }
    }, []);

    useEffect(() => {
        fetchSystemStatus();
    }, [fetchSystemStatus]);

    // Listen for refresh events from the App.js refresh button
    useEffect(() => {
        const handleRefreshEvent = () => {
            fetchSystemStatus();
        };

        window.addEventListener('refreshDashboardData', handleRefreshEvent);

        return () => {
            window.removeEventListener('refreshDashboardData', handleRefreshEvent);
        };
    }, [fetchSystemStatus]);

    return (
        <Layout>
            <Layout.Section>
                <div className="dashboard-main-wrapper" style={{ minHeight: '100vh', width: '100%' }}>
                    <div style={{ maxWidth: 1400, margin: '0 auto' }}>

                        {/* Warning messages for overloaded GPUs */}
                        {(((systemStatus?.runpodPreviewer?.currentQueue || systemStatus?.runpodPreviewer?.raw?.jobs?.inQueue || 0) > 10) || (systemStatus?.runpodPreviewer?.gpus != null && systemStatus.runpodPreviewer.gpus < 5)) && (
                            <div style={{
                                marginBottom: 16,
                                backgroundColor: '#fce7f3',
                                padding: 5,
                                borderRadius: 8,
                                color: '#d63384'
                            }}>
                                <InlineStack align="left" gap="10">
                                    <LiveIcon />
                                    <Text tone="warning" style={{ paddingLeft: 10, color: '#d63384' }}>
                                        {systemStatus?.runpodPreviewer?.gpus != null && systemStatus.runpodPreviewer.gpus < 5
                                            ? 'Previewer GPUs below 5'
                                            : 'GPU of Previewer is Overloaded'}
                                    </Text>
                                </InlineStack>
                            </div>
                        )}
                        {(systemStatus?.runpodGeneration?.currentQueue || systemStatus?.runpodGeneration?.raw?.jobs?.inQueue || 0) > 10 && (
                            <div style={{
                                marginBottom: 16,
                                backgroundColor: '#fce7f3',
                                padding: 5,
                                borderRadius: 8,
                                color: '#d63384'
                            }}>
                                <InlineStack align="left" gap="10">
                                    <LiveIcon style={{ marginRight: 10 }} />
                                    <Text tone="warning" style={{ paddingLeft: 10, marginLeft: 10, color: '#d63384' }}>GPU of Generation is Overloaded</Text>
                                </InlineStack>
                            </div>
                        )}

                        {/* Credits Row */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 32, marginBottom: 32 }}>
                            {/* Credits BlackForest Card */}
                            <Card style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.07)', borderRadius: 16, background: '#fff', minHeight: 200 }}>
                                <div className="dashboard-card-header" style={{ marginBottom: 24 }}>
                                    <Text variant="headingMd" as="h2" fontWeight="bold">Credits " BlackForest "</Text>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                    <div>
                                        <Text variant="bodySm" tone="subdued">Remaining Credits</Text>
                                        <span className="dashboard-number">{systemStatus?.credits?.bfl?.remainingCredits != null ? systemStatus.credits.bfl.remainingCredits : '-'}</span>
                                    </div>
                                    <div>
                                        <Text variant="bodySm" tone="subdued">Remaining Balance</Text>
                                        <span className="dashboard-number">${systemStatus?.credits?.bfl?.remainingBalanceUSD != null ? systemStatus.credits.bfl.remainingBalanceUSD : '-'}</span>
                                    </div>
                                    <div>
                                        <Text variant="bodySm" tone="subdued">Remaining Re-Generations</Text>
                                        <span className="dashboard-number">{systemStatus?.credits?.bfl?.remainingReGenerations != null ? Math.round(systemStatus.credits.bfl.remainingReGenerations) : '-'}</span>
                                    </div>
                                </div>
                            </Card>

                            {/* Credits ComfyUI Card */}
                            <Card style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.07)', borderRadius: 16, background: '#fff', minHeight: 200 }}>
                                <div className="dashboard-card-header" style={{ marginBottom: 24 }}>
                                    <Text variant="headingMd" as="h2" fontWeight="bold">Credits " ComfyUI "</Text>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                    <div>
                                        <Text variant="bodySm" tone="subdued">Remaining Credits</Text>
                                        <span className="dashboard-number">{systemStatus?.credits?.comfy?.remainingCredits != null ? systemStatus.credits.comfy.remainingCredits : '-'}</span>
                                    </div>
                                    <div>
                                        <Text variant="bodySm" tone="subdued">Remaining Balance</Text>
                                        <span className="dashboard-number">${systemStatus?.credits?.comfy?.remainingBalanceUSD != null ? systemStatus.credits.comfy.remainingBalanceUSD : '-'}</span>
                                    </div>
                                    <div>
                                        <Text variant="bodySm" tone="subdued">Remaining Stories</Text>
                                        <span className="dashboard-number">{systemStatus?.credits?.comfy?.remainingStories != null ? Math.round(systemStatus.credits.comfy.remainingStories) : '-'}</span>
                                    </div>
                                </div>
                            </Card>
                        </div>

                        {/* Two 50% width cards for Previewer and Generation Statistics */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 32, marginBottom: 32 }}>
                            {/* Generation Content Card (formerly Previewer Statistics) */}
                            <Card style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.07)', borderRadius: 16, background: '#fff', minHeight: 320 }}>
                                <div className="dashboard-card-header" style={{ marginBottom: 24 }}>
                                    <Icon source={ImageMajor} color="highlight" style={{ marginRight: 16, fontSize: 32 }} />
                                    <Text variant="headingMd" as="h2" fontWeight="bold">Generation Content</Text>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                    <div>
                                        <Text variant="bodySm" tone="subdued">Stories</Text>
                                        <span className="dashboard-number">{systemStatus?.statistics?.generationStories?.total != null ? Math.round(systemStatus.statistics.generationStories.total) : '-'}</span>
                                    </div>
                                    <div>
                                        <Text variant="bodySm" tone="subdued">Scenes</Text>
                                        <span className="dashboard-number">{systemStatus?.statistics?.generationScenes?.total != null ? Math.round(systemStatus.statistics.generationScenes.total) : '-'}</span>
                                    </div>
                                </div>
                                <div style={{ marginTop: 24 }}>
                                    <Text variant="bodyMd" fontWeight="semibold">Recent Generated Kids</Text>
                                    <div style={{ display: 'flex', alignItems: 'center', marginTop: 8 }}>
                                        {(systemStatus?.recentActivity?.recentGenerations || []).slice(0, 10).map((item, idx) => (
                                            <img
                                                key={item._id}
                                                // Assuming recentGenerations has similar structure (kid_image, etc.)
                                                src={item.kid_image || item.image_url}
                                                alt={item.kid_name || 'Generated Image'}
                                                title={`${item.kid_name || 'Unknown'} - ${item.story_name || item.story?.title || ''}`}
                                                style={{
                                                    width: 44, height: 44, borderRadius: '50%',
                                                    border: '2px solid #fff',
                                                    marginLeft: idx === 0 ? 0 : -16,
                                                    boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
                                                    objectFit: 'cover',
                                                    background: '#f3f4f6',
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </Card>
                            {/* Generation Statistics Card */}
                            <Card style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.07)', borderRadius: 16, background: '#fff', minHeight: 320 }}>
                                <div className="dashboard-card-header" style={{ marginBottom: 24 }}>
                                    <Icon source={PlayMinor} color="warning" style={{ marginRight: 16, fontSize: 32 }} />
                                    <Text variant="headingMd" as="h2" fontWeight="bold">Generation Statistics</Text>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                    <div>
                                        <Text variant="bodySm" tone="subdued">Total</Text>
                                        <span className="dashboard-number">{systemStatus?.statistics?.generations?.total != null ? Math.round(systemStatus.statistics.generations.total) : '-'}</span>
                                    </div>
                                    <div>
                                        <Text variant="bodySm" tone="subdued">Pending</Text>
                                        <span className="dashboard-number">{systemStatus?.statistics?.generations?.pending != null ? Math.round(systemStatus.statistics.generations.pending) : '-'}</span>
                                    </div>
                                    <div>
                                        <Text variant="bodySm" tone="subdued">Completed</Text>
                                        <span className="dashboard-number">{systemStatus?.statistics?.generations?.completed != null ? Math.round(systemStatus.statistics.generations.completed) : '-'}</span>
                                    </div>
                                    <div>
                                        <Text variant="bodySm" tone="subdued">New</Text>
                                        <span className="dashboard-number">{systemStatus?.statistics?.generations?.new != null ? Math.round(systemStatus.statistics.generations.new) : '-'}</span>
                                    </div>
                                    <div>
                                        <Text variant="bodySm" tone="subdued">Help</Text>
                                        <span className="dashboard-number">{systemStatus?.statistics?.generations?.help != null ? Math.round(systemStatus.statistics.generations.help) : '-'}</span>
                                    </div>
                                    <div>
                                        <Text variant="bodySm" tone="subdued">Express</Text>
                                        <span className="dashboard-number">{systemStatus?.statistics?.generations?.express != null ? Math.round(systemStatus.statistics.generations.express) : '-'}</span>
                                    </div>
                                    <div>
                                        <Text variant="bodySm" tone="subdued">Out For Printing</Text>
                                        <span className="dashboard-number">{systemStatus?.statistics?.generations?.outForPrinting != null ? Math.round(systemStatus.statistics.generations.outForPrinting) : '-'}</span>
                                    </div>
                                    <div>
                                        <Text variant="bodySm" tone="subdued">Completion Rate</Text>
                                        <Badge tone="info" size="large" style={{ fontSize: 18 }}>
                                            {systemStatus?.statistics?.generations?.completionRate
                                                ? (typeof systemStatus.statistics.generations.completionRate === 'string'
                                                    ? systemStatus.statistics.generations.completionRate
                                                    : Math.round(systemStatus.statistics.generations.completionRate) + '%')
                                                : '-'}
                                        </Badge>
                                    </div>
                                </div>
                            </Card>
                        </div>

                        {/* System card row: 1 column, 100% width */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 32 }}>
                            <Card style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.07)', borderRadius: 16, padding: 32, background: '#fff', minHeight: 320 }}>
                                <div className="dashboard-card-header" style={{ marginBottom: 24 }}>
                                    <Icon source={DatabaseIcon} color="base" style={{ fontSize: 32 }} />
                                    <Text variant="headingMd" as="h2" fontWeight="bold">System</Text>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                    <div>
                                        <Text variant="bodySm" tone="subdued">Uptime</Text>
                                        <span className="dashboard-number">{systemStatus?.system?.uptime != null ? `${Math.round(systemStatus.system.uptime / 60)}` : '-'}</span> min
                                    </div>
                                    <div>
                                        <Text variant="bodySm" tone="subdued">Node Version</Text>
                                        <span className="dashboard-number">{systemStatus?.system?.nodeVersion ?? '-'}</span>
                                    </div>
                                    <div>
                                        <Text variant="bodySm" tone="subdued">Platform</Text>
                                        <span className="dashboard-number">{systemStatus?.system?.platform ?? '-'}</span>
                                    </div>
                                    <div>
                                        <Text variant="bodySm" tone="subdued">Memory RSS</Text>
                                        <span className="dashboard-number">{systemStatus?.system?.memory?.rss != null ? `${Math.round(systemStatus.system.memory.rss / (1024 * 1024))}` : '-'}</span> MB
                                    </div>
                                    <div>
                                        <Text variant="bodySm" tone="subdued">Total Memory</Text>
                                        <span className="dashboard-number">{systemStatus?.system?.totalMemory != null ? `${Math.round(systemStatus.system.totalMemory / (1024 * 1024 * 1024))}` : '-'}</span> GB
                                    </div>
                                    <div>
                                        <Text variant="bodySm" tone="subdued">Total CPUs</Text>
                                        <span className="dashboard-number">{systemStatus?.system?.totalCPUs != null ? Math.round(systemStatus.system.totalCPUs) : '-'}</span>
                                    </div>

                                    <div>
                                        <Text variant="bodySm" tone="subdued">Memory Stats</Text>
                                        <span className="dashboard-number">
                                            {systemStatus?.system?.memStats ?
                                                `Total: ${systemStatus.system.memStats.totalMB} MB, Used: ${systemStatus.system.memStats.usedMB} MB, Unused: ${systemStatus.system.memStats.unusedMB} MB, Used: ${systemStatus.system.memStats.percentUsed}` :
                                                '-'}
                                        </span>
                                    </div>
                                    <div>
                                        <Text variant="bodySm" tone="subdued">CPU Stats</Text>
                                        <span className="dashboard-number">
                                            {systemStatus?.system?.cpuStats ?
                                                `Used: ${systemStatus.system.cpuStats.usedPercent}, Idle: ${systemStatus.system.cpuStats.idlePercent}, User: ${systemStatus.system.cpuStats.userPercent}, Sys: ${systemStatus.system.cpuStats.sysPercent}` :
                                                '-'}
                                        </span>
                                    </div>
                                    <div>
                                        <Text variant="bodySm" tone="subdued">Last Fetch Time</Text>
                                        <span className="dashboard-number">{systemStatus?.system?.timestamp ? new Date(systemStatus.system.timestamp).toLocaleString() : '-'}</span>
                                    </div>
                                </div>
                            </Card>
                        </div>
                    </div>
                </div>
            </Layout.Section>
        </Layout>
    );
}

export default DashboardPage;